<template>
  <transition mode="out-in" name="sf-collapse-top">
    <div
      v-if="lastNotification"
      ref="notificationRef"
      class="bg-success-light min-h-[40px] flex items-center py-2 justify-between"
      :class="typeClasses"
      data-test-id="notifications__container"
    >
      <div
        class="flex max-w-(--breakpoint-xl) h-full m-auto px-4 justify-between items-center w-full"
      >
        <div class="flex justify-center items-center w-full">
          <div
            v-if="lastNotification.htmlContent"
            v-html="lastNotification.htmlContent"
          ></div>
          <template v-else>
            <div class="flex gap-4">
              <span class="flex items-center">
                <component
                  :is="icons[lastNotification.type] || 'div'"
                  v-if="lastNotification.showIcon"
                  :height="24"
                  :width="24"
                  :class="iconClasses"
                />
              </span>

              <div
                class="leading-tight text-sm md:text-base md:leading-6 flex items-center"
              >
                {{ lastNotification.message }}
              </div>
            </div>
          </template>
        </div>
        <div class="flex align-center">
          <button @click="handleCloseNotification">
            <IconXLg class="w-5" />
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import type { Component } from 'vue'
import { computed, nextTick, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import InfoCircleFill from '~/assets/icons/info-circle-fill.svg?component'
import CheckIcon from '~/assets/icons/check-icon.svg?component'
import type { KftNotificationType } from '../../../stores/notifications'
import { useNotificationsStore } from '../../../stores/notifications'
import IconExclamationOctagonFill from '~/components/icons/IconExclamationOctagonFill.vue'
import { IconExclamationTriangleFill } from '#components'

const store = useNotificationsStore()
const { notifications } = storeToRefs(store)
const { handleCloseNotification, setNotificationHeight } = store

const notificationRef = ref<HTMLElement>()

const lastNotification = computed(() => {
  return notifications.value.length ? notifications.value.at(-1) : null
})

// watch for changes in the notification height
watch(
  [lastNotification, notificationRef],
  async () => {
    await nextTick()
    if (notificationRef.value && lastNotification.value) {
      const height = notificationRef.value.offsetHeight
      setNotificationHeight(height)
    } else {
      setNotificationHeight(0)
    }
  },
  { immediate: true }
)

const typeClasses = computed(() => {
  if (!lastNotification.value) return {}
  return {
    'bg-teal-50 text-teal-600': lastNotification.value.type === 'INFO',
    'bg-danger-light text-gray-700': lastNotification.value.type === 'ERROR',
    'bg-success-light text-gray-700': lastNotification.value.type === 'SUCCESS',
    'bg-warning-light text-gray-700 ':
      lastNotification.value.type === 'WARNING',
  }
})

const icons: Record<KftNotificationType, Component> = {
  INFO: InfoCircleFill,
  ERROR: IconExclamationTriangleFill,
  SUCCESS: CheckIcon,
  WARNING: IconExclamationOctagonFill,
}

const iconClasses = computed(() => {
  if (!lastNotification.value) return {}
  return {
    'text-success': lastNotification.value.type === 'INFO',
    'bg-danger-light text-danger': lastNotification.value.type === 'ERROR',
    'text-lime-700': lastNotification.value.type === 'SUCCESS',
    'bg-warning-light text-yellow-500':
      lastNotification.value.type === 'WARNING',
  }
})
</script>

<style scoped>
:deep(a) {
  text-decoration: underline;
  font-weight: 500;
}
</style>
